import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { ExternalLinkIcon } from 'lucide-react';
import {
  BackLink,
  DataTable,
  EmptyState,
  ErrorNotice,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  StatTile,
  type DataTableSort,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { money } from '@/lib/report-window';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';
import {
  REP_SEGMENT,
  REP_SORT_KEYS,
  SALES_QUERY_KEY,
  VALUE_KIND,
  compactMoney,
  dateLabel,
  daysAgoLabel,
  hoursLabel,
  percent,
  readRepOpportunities,
  readSalesScorecard,
  salesTimeframeSearch,
  salesforceOpportunityUrl,
  type RepOpportunityRow,
  type RepSegment,
} from '@/lib/sales';
import { RevenueTrend } from '@/components/sales-revenue-trend';
import { SalesTimeframePicker, useSalesTimeframe } from '@/components/sales-timeframe';
import { LossReasons } from '@/components/sales-loss-reasons';
import { RepProfitabilitySection, useCanSeeSalesProfit } from '@/components/sales-profitability';

/** The segment lives in the URL (`?segment=`) like the search and page. */
const SEGMENT_PARAM = 'segment';

const SEGMENT_LABELS: Readonly<Record<RepSegment, string>> = {
  [REP_SEGMENT.open]: 'Open',
  [REP_SEGMENT.won]: 'Won',
  [REP_SEGMENT.lost]: 'Lost',
  [REP_SEGMENT.unqualified]: 'Unqualified',
};

/** Latest close date first. */
const DEFAULT_SORT: DataTableSort = { key: 'closeDate', direction: 'desc' };

function isSegment(value: string | null): value is RepSegment {
  return value !== null && (Object.values(REP_SEGMENT) as string[]).includes(value);
}

/** The value cell: the signed contract, or the proposed-program average. */
/** The account and first lead under an opportunity's name, in the table and on its phone card. */
function opportunitySubtitle(row: RepOpportunityRow): string {
  const lead = row.leads[0];
  return (
    [row.accountName, lead ? `Lead: ${lead.name || lead.company} (${dateLabel(lead.createdAt)})` : '']
      .filter(Boolean)
      .join(' · ') || 'No account'
  );
}

/** The opportunity's stage, colored by won / open / lost. */
function StagePill({ row }: { readonly row: RepOpportunityRow }): React.ReactElement {
  return (
    <Pill tone={row.segment === REP_SEGMENT.won ? 'success' : row.segment === REP_SEGMENT.open ? 'info' : 'neutral'}>
      {row.stageName}
    </Pill>
  );
}

function ValueCell({ row }: { readonly row: RepOpportunityRow }): React.ReactElement {
  if (row.value === null) {
    return <span className="text-portal-muted">{row.flagged ? 'Flagged, over $150k' : 'Not priced'}</span>;
  }
  return (
    <>
      <span className="block">{money.format(row.value)}</span>
      <span className="block text-portal-micro text-portal-muted">
        {row.valueKind === VALUE_KIND.contract ? 'Contract' : 'Program average'}
      </span>
    </>
  );
}

/**
 * Analytics → Sales → one rep: their scorecard over the timeframe carried
 * from the dashboard (`?range=`, `from`, `to`), their 12-month revenue, and
 * every opportunity they own in one table with Open / Won / Lost /
 * Unqualified segments, the value, the leads it converted from, and how long
 * since it was last worked.
 *
 * @returns The rep page.
 */
export function SalesRepPage(): React.ReactElement {
  const { ownerId = '' } = useParams<{ ownerId: string }>();
  const [params, setParams] = useSearchParams();
  const segmentParam = params.get(SEGMENT_PARAM);
  const segment: RepSegment = isSegment(segmentParam) ? segmentParam : REP_SEGMENT.open;
  const { search, setSearch, debounced } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(REP_SORT_KEYS, DEFAULT_SORT);
  const canSeeProfit = useCanSeeSalesProfit();

  const timeframeState = useSalesTimeframe();
  const { timeframe } = timeframeState;
  const scorecard = useQuery({
    queryKey: SALES_QUERY_KEY.scorecard(timeframe),
    queryFn: ({ signal }) => readSalesScorecard(timeframe, signal),
    placeholderData: keepPreviousData,
  });
  const request = {
    segment,
    page,
    ...(debounced ? { q: debounced } : {}),
    ...(sort ? { sort: sort.key, dir: sort.direction } : {}),
  };
  const opportunities = useQuery({
    queryKey: SALES_QUERY_KEY.rep(ownerId, request),
    queryFn: ({ signal }) => readRepOpportunities(ownerId, request, signal),
    placeholderData: keepPreviousData,
  });

  function setSegment(next: RepSegment): void {
    setParams(
      (previous) => {
        const nextParams = new URLSearchParams(previous);
        if (next === REP_SEGMENT.open) nextParams.delete(SEGMENT_PARAM);
        else nextParams.set(SEGMENT_PARAM, next);
        nextParams.delete('page');
        return nextParams;
      },
      { replace: true },
    );
  }

  const rep = scorecard.data?.reps.find((entry) => entry.ownerId === ownerId);
  const ownerName = rep?.ownerName || opportunities.data?.owner.ownerName || 'Rep';
  const counts = opportunities.data?.counts;
  const target = scorecard.data?.winRateTarget ?? 0.25;
  const versus = scorecard.data?.previousWindow.label ?? '';
  const rows = opportunities.data?.rows ?? [];

  return (
    <section className="animate-portal-rise">
      <BackLink to={`/analytics/sales${salesTimeframeSearch(timeframe)}`}>Sales</BackLink>
      <PageHeading
        eyebrow="SALES"
        title={ownerName}
        subtitle="Every opportunity this rep owns, with its value and how long since it was last worked."
      />

      <div className="mb-4">
        <SalesTimeframePicker state={timeframeState} window={scorecard.data?.window} />
      </div>

      {rep ? (
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
          <StatTile
            label="Booked"
            value={money.format(rep.period.revenue)}
            hint={`${rep.period.bookings} bookings · ${money.format(rep.previous.revenue)} ${versus}`}
          />
          <StatTile
            label="Win rate"
            value={percent(rep.period.winRate)}
            hint={`Adjusted ${percent(rep.period.adjustedWinRate)} · target ${percent(target)} · ${percent(rep.previous.winRate)} ${versus}`}
          />
          <StatTile
            label="Open pipeline today"
            value={compactMoney(rep.open.pipelineEstimate)}
            hint={`${rep.open.open} open · ${rep.open.priced} priced`}
          />
          <StatTile
            label="Stale open deals today"
            value={rep.open.stale.toLocaleString('en-US')}
            hint={`${rep.open.pastClose} past close date · ${rep.open.shouldClose} should be closed`}
            downIsGood
          />
          <StatTile
            label="Average deal value"
            value={rep.period.averageDealValue === null ? 'n/a' : money.format(rep.period.averageDealValue)}
            hint={`${rep.previous.averageDealValue === null ? 'n/a' : money.format(rep.previous.averageDealValue)} ${versus}`}
          />
          <StatTile
            label="Average first touch"
            value={hoursLabel(rep.firstTouch.averageHours)}
            hint={`Median ${hoursLabel(rep.firstTouch.medianHours)} · ${percent(rep.firstTouch.share)} within 24 hrs · ${rep.firstTouch.untouched} not touched`}
            downIsGood
          />
        </div>
      ) : null}

      {scorecard.data && rep ? (
        <div className="mt-6">
          <RevenueTrend scorecard={scorecard.data} reps={[rep]} title={`${ownerName}: booked revenue, last 12 months`} />
        </div>
      ) : null}

      {rep ? (
        <div className="mt-6">
          <LossReasons scopes={[{ key: rep.ownerName, label: rep.ownerName, losses: rep.losses }]} />
        </div>
      ) : null}

      <div className="mt-6 mb-3 flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl<RepSegment>
          label="Which opportunities to show"
          value={segment}
          onChange={setSegment}
          options={(Object.keys(SEGMENT_LABELS) as RepSegment[]).map((key) => ({
            key,
            label: counts ? `${SEGMENT_LABELS[key]} (${counts[key].toLocaleString('en-US')})` : SEGMENT_LABELS[key],
          }))}
        />
        <SearchInput
          label="Search opportunities"
          placeholder="Opportunity, account or stage"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-80 min-w-56"
        />
      </div>

      {opportunities.isError ? (
        <ErrorNotice title="Opportunities unavailable" message={errorMessage(opportunities.error)} />
      ) : opportunities.isPending ? (
        <SkeletonTable rows={8} />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            title={debounced ? 'No opportunities match' : `No ${SEGMENT_LABELS[segment].toLowerCase()} opportunities`}
            message={
              debounced
                ? 'Try a different search, or another segment.'
                : 'Opportunities this rep owns in Salesforce show here once the next sync runs.'
            }
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Opportunity', sortKey: 'name' },
              { label: 'Stage', sortKey: 'stage' },
              { label: 'Created', sortKey: 'created' },
              { label: 'Close date', sortKey: 'closeDate' },
              { label: 'Value', sortKey: 'value' },
              { label: 'Last email or call', sortKey: 'touch' },
              { label: 'Last stage change', sortKey: 'stageChange' },
              'Salesforce',
            ]}
            rightAlignedHeaders={[4, 5, 6]}
            visuallyHiddenHeaders={[7]}
            sort={sort}
            onSort={onSort}
            minWidth={1160}
            cards={rows.map((row) => ({
              key: row.opportunityId,
              title: row.name || row.opportunityId,
              subtitle: opportunitySubtitle(row),
              fields: [
                { label: 'Stage', value: <StagePill row={row} /> },
                { label: 'Value', value: <ValueCell row={row} /> },
                { label: 'Created', value: dateLabel(row.createdAt) },
                { label: 'Close date', value: dateLabel(row.closeDate) },
                { label: 'Last email or call', value: row.daysSinceTouch === null ? 'None logged' : daysAgoLabel(row.daysSinceTouch) },
                { label: 'Last stage change', value: daysAgoLabel(row.daysSinceStageChange) },
              ],
              action: (
                <a
                  href={salesforceOpportunityUrl(row.opportunityId)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-10 items-center gap-1 text-portal-small text-brand"
                >
                  <ExternalLinkIcon className="size-4" aria-hidden="true" /> Open in Salesforce
                </a>
              ),
            }))}
          >
            {rows.map((row) => {
              return (
                <tr key={row.opportunityId} className="border-t border-portal-line align-middle">
                  <td className="p-3 text-portal-small">
                    <span className="block font-bold text-portal-ink">{row.name || row.opportunityId}</span>
                    <span className="block text-portal-micro text-portal-muted">{opportunitySubtitle(row)}</span>
                  </td>
                  <td className="p-3 text-portal-small whitespace-nowrap">
                    <StagePill row={row} />
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{dateLabel(row.createdAt)}</td>
                  <td className="p-3 text-portal-small text-portal-muted whitespace-nowrap">{dateLabel(row.closeDate)}</td>
                  <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
                    <ValueCell row={row} />
                  </td>
                  <td className="p-3 text-right text-portal-small tabular-nums whitespace-nowrap">
                    {row.daysSinceTouch === null ? (
                      <span className="text-portal-muted">None logged</span>
                    ) : (
                      <>
                        <span className="block">{daysAgoLabel(row.daysSinceTouch)}</span>
                        <span className="block text-portal-micro text-portal-muted">{dateLabel(row.lastTouchAt)}</span>
                      </>
                    )}
                  </td>
                  <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted whitespace-nowrap">
                    {daysAgoLabel(row.daysSinceStageChange)}
                  </td>
                  <td className="p-3 text-right">
                    <a
                      href={salesforceOpportunityUrl(row.opportunityId)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-brand"
                      aria-label={`Open ${row.name || row.opportunityId} in Salesforce`}
                    >
                      <ExternalLinkIcon className="size-4" aria-hidden="true" />
                    </a>
                  </td>
                </tr>
              );
            })}
          </DataTable>
          <Pagination
            page={opportunities.data?.page ?? page}
            totalPages={opportunities.data?.totalPages ?? 1}
            onChange={setPage}
            summary={`${opportunities.data?.total.toLocaleString('en-US') ?? 0} ${SEGMENT_LABELS[segment].toLowerCase()} opportunities`}
            className="mt-3"
          />
        </>
      )}

      {canSeeProfit && ownerId ? <RepProfitabilitySection ownerId={ownerId} /> : null}
    </section>
  );
}
